import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  TablePagination,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Add,
  Delete,
  Edit,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useTextSources } from '@/hooks/useTextSources';
import { format } from 'date-fns';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import AttachSourceToAgentsDialog from '@/components/admin/AttachSourceToAgentsDialog';
import adminApi from '@/services/admin.api';

interface AttachedAgent {
  _id: string;
  name: string;
  topics?: string[];
}

const TextSources = () => {
  const {
    items,
    loading,
    page,
    setPage,
    total,
    PAGE_SIZE,
    selectedId,
    form,
    select,
    setTitle,
    setTextContent,
    save,
    remove,
    saving,
    error,
    success,
    resetForm,
  } = useTextSources();

  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [attachTarget, setAttachTarget] = useState<{ id: string; title: string } | null>(null);
  const [textAgents, setTextAgents] = useState<Record<string, AttachedAgent[]>>({});
  const [loadingTextAgents, setLoadingTextAgents] = useState<Record<string, boolean>>({});
  const [detachConfirm, setDetachConfirm] = useState<{
    open: boolean;
    textId: string | null;
    textTitle: string;
    agentId: string | null;
    agentName: string;
  }>({ open: false, textId: null, textTitle: '', agentId: null, agentName: '' });

  const handleOpen = (id?: string) => {
    select(id || null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const handleSave = async () => {
    await save();
    if (!error) {
      setOpen(false);
    }
  };

  const promptDelete = (id: string) => {
    setDeleteId(id);
    setOpenDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      await remove(deleteId);
      setOpenDeleteConfirm(false);
      setDeleteId(null);
      if (selectedId === deleteId) {
        handleClose();
      }
    }
  };

  // Load attached agents for every row so the list shows them directly,
  // without needing to open each text source.
  useEffect(() => {
    const loadAgentsForItems = async () => {
      for (const item of items) {
        if (!textAgents[item._id] && !loadingTextAgents[item._id]) {
          setLoadingTextAgents((prev) => ({ ...prev, [item._id]: true }));
          try {
            const agents = await adminApi.getTextSourceAgents(item._id);
            setTextAgents((prev) => ({ ...prev, [item._id]: agents }));
          } catch {
            setTextAgents((prev) => ({ ...prev, [item._id]: [] }));
          } finally {
            setLoadingTextAgents((prev) => ({ ...prev, [item._id]: false }));
          }
        }
      }
    };

    if (items.length > 0) {
      void loadAgentsForItems();
    }
  }, [items, textAgents, loadingTextAgents]);

  const refreshAgentsFor = useCallback(async (textId: string) => {
    try {
      const agents = await adminApi.getTextSourceAgents(textId);
      setTextAgents((prev) => ({ ...prev, [textId]: agents }));
    } catch {
      // keep previous state on failure
    }
  }, []);

  const handleDetachClick = (
    textId: string,
    textTitle: string,
    agentId: string,
    agentName: string
  ) => {
    setDetachConfirm({ open: true, textId, textTitle, agentId, agentName });
  };

  const handleConfirmDetach = useCallback(async () => {
    if (!detachConfirm.textId || !detachConfirm.agentId) return;
    try {
      await adminApi.detachTextSourceFromAgent(detachConfirm.agentId, detachConfirm.textId);
      await refreshAgentsFor(detachConfirm.textId);
      setDetachConfirm({ open: false, textId: null, textTitle: '', agentId: null, agentName: '' });
    } catch (err) {
      // Keep confirm open on failure; user can retry.
    }
  }, [detachConfirm.textId, detachConfirm.agentId, refreshAgentsFor]);

  const columns: GridColDef[] = [
    { field: 'title', headerName: 'Title', flex: 1 },
    { field: 'createdByName', headerName: 'Created By', width: 150 },
    {
      field: 'updatedAt',
      headerName: 'Last Updated',
      width: 180,
      valueFormatter: (params) => format(new Date(params.value), 'PPp'),
    },
    {
      field: 'attachedAgents',
      headerName: 'Attached Agents',
      flex: 1,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => {
        const agents = textAgents[params.row._id] || [];
        const isLoadingAgents = loadingTextAgents[params.row._id];

        if (isLoadingAgents) {
          return (
            <Typography variant="caption" color="text.secondary">
              Loading...
            </Typography>
          );
        }
        if (agents.length === 0) {
          return (
            <Typography variant="caption" color="text.secondary">
              Not attached to any agents
            </Typography>
          );
        }
        return (
          <Box display="flex" gap={0.5} flexWrap="wrap" sx={{ py: 0.5 }}>
            {agents.map((agent) => (
              <Chip
                key={agent._id}
                label={agent.name}
                size="small"
                onDelete={() =>
                  handleDetachClick(params.row._id, params.row.title, agent._id, agent.name)
                }
                deleteIcon={<CloseIcon />}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={() => handleOpen(params.row._id)} size="small" title="Edit">
            <Edit />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setAttachTarget({ id: params.row._id, title: params.row.title });
            }}
            size="small"
            title="Attach to agents"
          >
            <AttachFileIcon />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              promptDelete(params.row._id);
            }}
            size="small"
            color="error"
            title="Delete"
          >
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Text Sources</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
          Add New
        </Button>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {/* Rows are paged server-side, so the grid's own footer is hidden in
              favour of the TablePagination below (which drives the hook's page). */}
          <DataGrid
            rows={items}
            columns={columns}
            getRowId={(row) => row._id}
            loading={loading}
            disableRowSelectionOnClick
            getRowHeight={() => 'auto'}
            hideFooter
          />
        </Box>
        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
          />
        )}
      </Card>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{selectedId ? 'Edit Text Source' : 'New Text Source'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Title"
              fullWidth
              value={form.title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={saving}
            />

            <Box sx={{ height: 400, mb: 4 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Content
              </Typography>
              <ReactQuill
                theme="snow"
                value={form.textContent}
                onChange={setTextContent}
                style={{ height: '300px' }}
                modules={{
                  toolbar: [[{ header: [1, 2, false] }], ['bold', 'italic'], ['clean']],
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          {selectedId && (
            <Button color="error" onClick={() => promptDelete(selectedId)} disabled={saving}>
              Delete
            </Button>
          )}
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Button onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </Box>
        </Box>
      </Dialog>

      <ConfirmDialog
        open={openDeleteConfirm}
        title="Confirm Delete"
        content="Are you sure you want to delete this text source? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={() => setOpenDeleteConfirm(false)}
        loading={saving}
      />

      <AttachSourceToAgentsDialog
        open={!!attachTarget}
        onClose={() => setAttachTarget(null)}
        onSuccess={() => {
          if (!attachTarget) return;
          void refreshAgentsFor(attachTarget.id);
        }}
        sourceIds={attachTarget ? [attachTarget.id] : []}
        sourceLabel="Text Source"
        sourceName={attachTarget?.title || ''}
        getAttachedAgents={adminApi.getTextSourceAgents}
        attachSourcesToAgents={adminApi.attachTextSourcesToAgents}
        detachAgentFromSource={adminApi.detachTextSourceFromAgent}
      />

      <ConfirmDialog
        open={detachConfirm.open}
        title="Detach Agent"
        content={`Are you sure you want to detach "${detachConfirm.agentName}" from "${detachConfirm.textTitle}"?`}
        confirmText="Detach"
        cancelText="Cancel"
        onConfirm={() => void handleConfirmDetach()}
        onCancel={() =>
          setDetachConfirm({ open: false, textId: null, textTitle: '', agentId: null, agentName: '' })
        }
      />
    </Box>
  );
};

export default TextSources;
